import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { API_BASE, STATUS_OPTIONS, pick, unwrap, statusMeta, fmtDate } from './routePlan/routePlanUtils';

const PAGE_SIZE_DEFAULT = 10;
const FETCH_TAKE = 500;

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'name' },
  representativeName: { type: 'text', odataField: 'representativeName' },
  status: { type: 'select', odataField: 'status', options: STATUS_OPTIONS },
};

function mapRow(r) {
  return {
    id: pick(r, 'id', 'Id'),
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    startDate: pick(r, 'startDate', 'StartDate') ?? null,
    endDate: pick(r, 'endDate', 'EndDate') ?? null,
    // #800 Kapsam çoklu; liste yalnız adları gösterir. Boş = plan tüm distribütörleri kapsar.
    vendorNames: pick(r, 'vendorNames', 'VendorNames') ?? [],
    status: pick(r, 'status', 'Status') ?? 'Draft',
    customerCount: pick(r, 'customerCount', 'CustomerCount') ?? 0,
    representativeCount: pick(r, 'representativeCount', 'RepresentativeCount') ?? 0,
    representativeName: pick(r, 'representativeName', 'RepresentativeName') ?? '',
    updatedDate: pick(r, 'updatedDate', 'UpdatedDate') ?? null,
  };
}

export function RoutePlansPage() {
  usePageMeta('Rut Tanımları', 'Saha temsilcilerinin döngüsel ziyaret planlarını (rut) tanımlayın.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=${FETCH_TAKE}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const items = unwrap(payload);
      const mapped = (Array.isArray(items) ? items : []).map(mapRow);
      setAllRows(mapped);
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const matches = (row) => {
      if (needle && !String(row.name ?? '').toLocaleLowerCase('tr-TR').includes(needle)) return false;
      const sf = columnFilters.status;
      if (sf && sf.value && row.status !== sf.value) return false;
      const nf = columnFilters.name;
      if (nf && nf.value && !String(row.name ?? '').toLocaleLowerCase('tr-TR').includes(String(nf.value).toLocaleLowerCase('tr-TR'))) return false;
      const rf = columnFilters.representativeName;
      if (rf && rf.value && !String(row.representativeName ?? '').toLocaleLowerCase('tr-TR').includes(String(rf.value).toLocaleLowerCase('tr-TR'))) return false;
      return true;
    };
    return allRows.filter(matches).sort((a, b) => {
      const av = a.updatedDate ? new Date(a.updatedDate).getTime() : 0;
      const bv = b.updatedDate ? new Date(b.updatedDate).getTime() : 0;
      return bv - av;
    });
  }, [allRows, filterText, columnFilters]);

  const totalCount = filteredSorted.length;
  const rows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => { setColumnFilters((p) => ({ ...p, [columnId]: next })); setCurrentPage(1); };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((p) => { const c = { ...p }; delete c[columnId]; return c; });
    setCurrentPage(1);
  };

  const openNew = () => navigate('/route-plans/new');
  const openDetail = (row) => { if (row?.id) navigate(`/route-plans/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${selected.id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Rut planı silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Rut Planı', meta: { filterType: 'text' },
      cell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>🗺️</span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{row.original.name || '-'}</span>
            {row.original.textId && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{row.original.textId}</span>
            )}
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'period', header: 'Geçerlilik',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
          <span aria-hidden="true">🗓</span>{fmtDate(row.original.startDate)} – {fmtDate(row.original.endDate)}
        </span>
      ),
    }),
    columnHelper.accessor('customerCount', {
      header: 'Müşteri', enableColumnFilter: false,
      cell: (info) => <StatusBadge tone="neutral">{Number(info.getValue() || 0)} müşteri</StatusBadge>,
    }),
    columnHelper.accessor('representativeCount', {
      header: 'Temsilci', enableColumnFilter: false,
      cell: (info) => <StatusBadge tone="neutral">{Number(info.getValue() || 0)} temsilci</StatusBadge>,
    }),
    // Talep 802: Ruta bağlı 1. satış temsilcisinin adı (çoklu ise yalnız ilki; boşsa "-").
    columnHelper.accessor('representativeName', {
      header: 'Satış Temsilcisi', enableSorting: true, meta: { filterType: 'text' },
      cell: ({ row }) => {
        const name = row.original.representativeName;
        const extra = Math.max(0, Number(row.original.representativeCount || 0) - 1);
        if (!name) return <span style={{ color: 'var(--text-secondary,#9ca3af)' }}>-</span>;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <span aria-hidden="true">👤</span>
            <span style={{ fontWeight: 600 }}>{name}</span>
            {extra > 0 && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary,#9ca3af)' }} title={`Bu ruta ${extra} temsilci daha atanmış`}>+{extra}</span>
            )}
          </span>
        );
      },
    }),
    // #800 Planın distribütör kapsamı (çoklu). Boşsa plan tüm distribütörleri kapsar →
    // "Tümü" yazılır; "-" yanıltıcı olurdu (kapsam eksik değil, bilinçli olarak sınırsız).
    columnHelper.accessor('vendorNames', {
      header: 'Distribütör', enableSorting: false, enableColumnFilter: false,
      cell: ({ row }) => {
        const names = row.original.vendorNames || [];
        if (names.length === 0) {
          return <StatusBadge tone="neutral">Tümü</StatusBadge>;
        }
        const rest = names.length - 1;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
            title={names.join(', ')}>
            <span style={{ fontWeight: 600 }}>{names[0]}</span>
            {rest > 0 && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary,#9ca3af)' }}>+{rest}</span>
            )}
          </span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const m = statusMeta(info.getValue()); return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    try {
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { field: 'name', headerName: 'Rut Planı' },
        { headerName: 'Başlangıç', valueGetter: ({ row }) => fmtDate(row.startDate) },
        { headerName: 'Bitiş', valueGetter: ({ row }) => fmtDate(row.endDate) },
        { field: 'customerCount', headerName: 'Müşteri' },
        { field: 'representativeCount', headerName: 'Temsilci' },
        { headerName: 'Satış Temsilcisi', valueGetter: ({ row }) => row.representativeName || '-' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusMeta(row.status).label },
      ];
      exportToExcel(filteredSorted, cols, `rut_planlari_${new Date().toISOString().slice(0, 10)}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Rut adı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Rut Tanımları"
        subtitle="Döngüsel ziyaret planlarını oluşturun: müşteri havuzu, hafta/gün programı ve temsilci ataması."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Ekle</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected.name}"</strong> rut planını silmek istediğinize emin misiniz?</p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: saving ? 0.6 : 1 }} onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default RoutePlansPage;
